import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Alert,
    LinearProgress,
    Chip
} from '@mui/material';
import { VacationRequest, VACATION_STATUS_COLORS, VACATION_STATUS_LABELS } from '../../types/collaborator';
import { createVacationRequest, getVacationRequests } from '../../services/collaborator';

// Dias corridos, ambos inclusivos. Negativo/zero quando a data final é anterior à inicial.
const calculateDays = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
};

const VacationsSection: React.FC = () => {
    const [vacationRequests, setVacationRequests] = useState<VacationRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [notes, setNotes] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);

    const fetchVacationRequests = async () => {
        try {
            setLoading(true);
            const data = await getVacationRequests();
            setVacationRequests(data);
            setError(null);
        } catch (err) {
            setError('Erro ao carregar solicitações de férias. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVacationRequests();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!startDate || !endDate) {
            return;
        }

        try {
            setSubmitError(null);
            await createVacationRequest({
                start_date: startDate,
                end_date: endDate,
                notes: notes || undefined
            });
            setOpenDialog(false);
            fetchVacationRequests();
            // Reset form
            setStartDate('');
            setEndDate('');
            setNotes('');
        } catch (err: any) {
            // Mantém o diálogo aberto com a mensagem do backend (ex.: data final antes da inicial)
            setSubmitError(err?.response?.data?.error || 'Erro ao criar solicitação de férias. Por favor, tente novamente.');
        }
    };

    if (loading) {
        return <LinearProgress />;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                    Férias
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenDialog(true)}
                >
                    Nova Solicitação
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Período</TableCell>
                            <TableCell align="center">Dias</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Observações</TableCell>
                            <TableCell>Data da Solicitação</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vacationRequests.map((request) => (
                            <TableRow key={request.id}>
                                <TableCell>
                                    {new Date(request.start_date).toLocaleDateString()} até{' '}
                                    {new Date(request.end_date).toLocaleDateString()}
                                </TableCell>
                                <TableCell align="center">
                                    {calculateDays(request.start_date, request.end_date)}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={VACATION_STATUS_LABELS[request.status] || request.status}
                                        color={VACATION_STATUS_COLORS[request.status] || 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {request.notes || '-'}
                                    {request.review_notes && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Parecer do RH: {request.review_notes}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {new Date(request.created_at).toLocaleDateString()}
                                </TableCell>
                            </TableRow>
                        ))}
                        {vacationRequests.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Nenhuma solicitação de férias encontrada.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>Nova Solicitação de Férias</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            {submitError && (
                                <Grid item xs={12}>
                                    <Alert severity="error">{submitError}</Alert>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data Inicial"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => { setStartDate(e.target.value); setSubmitError(null); }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data Final"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => { setEndDate(e.target.value); setSubmitError(null); }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>

                            {startDate && endDate && (
                                <Grid item xs={12}>
                                    {calculateDays(startDate, endDate) > 0 ? (
                                        <Alert severity="info">
                                            Período selecionado: {calculateDays(startDate, endDate)} dias
                                        </Alert>
                                    ) : (
                                        <Alert severity="warning">
                                            A data final não pode ser anterior à data inicial.
                                        </Alert>
                                    )}
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Observações"
                                    multiline
                                    rows={4}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!startDate || !endDate || calculateDays(startDate, endDate) <= 0}
                        >
                            Solicitar Férias
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default VacationsSection; 